const EMPTY_VALUE = 'Not provided';
const ATTACHED_VALUE = 'Attached';

function isAttachmentContent(value) {
  return typeof value === 'string' && /^(data:|gs:|blob:)/i.test(value.trim());
}

function attachmentName(value) {
  if (!value) return null;
  if (typeof value === 'object') {
    return value.file?.name || value.name || null;
  }
  if (typeof value !== 'string' || isAttachmentContent(value)) {
    return null;
  }
  const candidate = value.trim();
  return /^[^/\\\r\n]{1,240}\.[a-z0-9]{1,16}$/i.test(candidate) ? candidate : null;
}

export function formatPrintableAttachment(value) {
  if (value === null || value === undefined || value === '' || (
    Array.isArray(value) && value.length === 0
  )) {
    return EMPTY_VALUE;
  }

  const attachments = Array.isArray(value) ? value : [value];
  const labels = attachments.map(item => attachmentName(item) || ATTACHED_VALUE);
  return [...new Set(labels)].join('\n');
}

function stripMarkup(value) {
  return String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function humanizeIdentifier(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, letter => letter.toUpperCase());
}

function formatObject(value) {
  const fileName = value?.file?.name || (
    value?.name && (value?.content || value?.type) ? value.name : null
  );
  if (fileName) {
    return String(fileName);
  }
  if (Object.prototype.hasOwnProperty.call(value || {}, 'content')) {
    return ATTACHED_VALUE;
  }

  const lines = Object.entries(value || {})
    .filter(([key, item]) => key !== 'content' && item !== null && item !== undefined && item !== '')
    .map(([key, item]) => `${humanizeIdentifier(key)}: ${formatPrintableValue(item)}`);
  return lines.length > 0 ? lines.join('\n') : EMPTY_VALUE;
}

export function formatPrintableValue(value) {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (isAttachmentContent(value)) {
    return ATTACHED_VALUE;
  }
  if (Array.isArray(value)) {
    return value.length > 0
      ? value.map(item => formatPrintableValue(item)).join('\n')
      : EMPTY_VALUE;
  }
  if (typeof value === 'object') {
    return formatObject(value);
  }
  return String(value);
}

function questionType(question) {
  return typeof question?.getType === 'function' ? question.getType() : question?.questionType;
}

function questionToPrintItem(question, index) {
  const type = questionType(question);
  const label = stripMarkup(question?.title || question?.name);
  const description = stripMarkup(question?.description);

  if (type === 'html') {
    return {
      key: `note-${question?.name || index}`,
      kind: 'note',
      value: stripMarkup(question?.html)
    };
  }

  if (type === 'expression' && !question?.expression) {
    return {
      key: `heading-${question?.name || index}`,
      kind: 'heading',
      label,
      description
    };
  }

  const displayValue = question?.displayValue;
  return {
    key: `answer-${question?.name || index}`,
    kind: 'answer',
    label: label || humanizeIdentifier(question?.name) || 'Question',
    description,
    value: type === 'file'
      ? formatPrintableAttachment(question?.value ?? displayValue)
      : formatPrintableValue(
          displayValue === null || displayValue === undefined || displayValue === ''
            ? question?.value
            : displayValue
        )
  };
}

/**
 * Convert a populated SurveyJS model into a static, printable representation.
 * SurveyJS is still responsible for evaluating page and question visibility and
 * for converting stored choice values into their configured display labels.
 */
export function buildPrintableSurveySections(survey, customFieldValues = []) {
  if (!survey) {
    return [];
  }

  const allQuestions = typeof survey.getAllQuestions === 'function'
    ? survey.getAllQuestions(false)
    : [];
  const configuredNames = new Set(allQuestions.map(question => String(question?.name)));
  const pages = survey.visiblePages || survey.pages || [];
  const seenQuestions = new Set();

  const sections = pages.map((page, pageIndex) => {
    const questions = typeof page?.getAllQuestions === 'function'
      ? page.getAllQuestions(true)
      : (page?.questions || []).filter(question => question?.isVisible !== false);
    const items = questions
      .filter(question => {
        const key = question?.name || question;
        if (seenQuestions.has(key)) return false;
        seenQuestions.add(key);
        return true;
      })
      .map(questionToPrintItem)
      .filter(item => item.kind !== 'note' || item.value);

    return {
      key: `page-${page?.name || pageIndex}`,
      title: stripMarkup(page?.title),
      description: stripMarkup(page?.description),
      items
    };
  }).filter(section => section.items.length > 0 || section.title || section.description);

  const unconfiguredItems = (customFieldValues || [])
    .filter(fieldValue => fieldValue?.id && !configuredNames.has(String(fieldValue.id)))
    .map((fieldValue, index) => ({
      key: `unconfigured-${fieldValue.id}-${index}`,
      kind: 'answer',
      label: stripMarkup(fieldValue.label) || humanizeIdentifier(fieldValue.id) || 'Additional field',
      description: `Stored field ID: ${fieldValue.id}`,
      value: formatPrintableValue(fieldValue.value)
    }));

  if (unconfiguredItems.length > 0) {
    sections.push({
      key: 'unconfigured-fields',
      title: 'Additional stored answers',
      description: '',
      items: unconfiguredItems
    });
  }

  return sections;
}
